// Boomer: the 808-style drum machine character (drum machine + 808 bass pads).
//   boomer       — wide, low charcoal chassis; a crown of little silver/cream knobs along the top
//                  edge; knob eyes; the row of step keys in four colour groups (vermillion,
//                  orange, yellow, cream) is its toothy grin; rubber feet; power-cable tail.
//   boomer_bass  — same machine, heavier and lower: big knob eyes poking up over the top edge
//                  and a fat round sub-woofer belly that bulges out below the chassis.
// Behaviour: the step keys glow in a slow soft chase at idle, chase fast on hit / hold (all keys
// flash on the onset), the body thumps with an extra-big squash (geo.squashScale), accents pop
// sound-wave rings, and the bass speaker cone pumps on hits and buzzes while a note is held.
// Idle motion stays slow (≤ ~1 Hz); fast motion only on hit / accent / hold / cheer.
import { Container, Graphics } from "pixi.js";
import { INK, mix } from "../palette";
import { Rng } from "../lib/rng";
import type { CreatureGenome } from "./genome";
import { disc, noodle } from "./draw";
import {
  STEP_COLORS,
  drawBackSpikes,
  drawBlush,
  drawKnob,
  drawMohawk,
  drawTufts,
  eyeLayout,
  limb,
  newAnatomyParts,
  pal,
  sides,
  stepKeyLayout,
  type Anatomy,
  type Colors,
  type Geo,
} from "./parts";

const SILVER = 0xc9ccd3;
const CREAM = 0xf4efe1;
const RUBBER = 0x1f1e23;
const CABLE = 0x4a4850;

export function boomer(gn: CreatureGenome, H: number, ow: number): Anatomy {
  return build(gn, H, ow, false);
}

export function boomerBass(gn: CreatureGenome, H: number, ow: number): Anatomy {
  return build(gn, H, ow, true);
}

function build(gn: CreatureGenome, H: number, ow: number, bass: boolean): Anatomy {
  const tr = gn.traits;
  const legH = H * (bass ? 0.1 : 0.09) * gn.limbLength;
  const ry = H * (bass ? 0.29 : 0.26) * gn.heightScale;
  const rx = ry * gn.bodyAspect * (bass ? 1.2 : 1.15);
  const cy = -legH - ry;
  const corner = H * 0.07;

  // Eyes (knob dials). Drum machine: inside the upper panel. Bass: poking up over the top edge.
  const e0 = eyeLayout(gn, H, rx * (bass ? 0.75 : 0.8), cy, 1.05);
  const eyeR = Math.min(e0.eyeR * (bass ? 1.1 : 1), ry * (bass ? 0.45 : 0.5));
  const eyeDx = Math.min(Math.max(e0.eyeDx, eyeR * (bass ? 1.35 : 1.25)), rx - eyeR * 1.05);
  const eyeY = bass ? cy - ry + gn.eyeHeight * H * 0.1 : cy - ry * 0.25 + gn.eyeHeight * H * 0.25;

  // Step-key grin (wider machines get 12 keys instead of 8).
  const nKeys = tr.width >= 0.7 ? 12 : 8;
  const mouthW = Math.min(rx * (bass ? 1.25 : 1.6) * (0.85 + (gn.mouthWidth - 0.5) * 0.5), rx * 1.75);
  const mouthY = bass ? cy - ry * 0.28 : cy + ry * 0.52;
  const keys = stepKeyLayout(mouthW, nKeys, H);

  // Bass speaker belly.
  const spR = ry * 0.68;
  const spY = Math.min(cy + ry * 0.62, -spR - H * 0.012);

  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: cy,
    halfW: rx + H * (bass ? 0.06 : 0.05),
    headTopY: bass ? Math.min(eyeY - eyeR, cy - ry) : cy - ry - H * 0.035,
    headW: rx * (bass ? 1.1 : 1.3),
    topY: (bass ? eyeY - eyeR : cy - ry - H * 0.04) - H * 0.04,
    eyeY,
    eyeDx,
    eyeR,
    mouthY,
    mouthW,
    neckY: cy + ry * 0.92,
    neckHalfW: rx * 1.01,
    floats: false,
    eyeStyle: "knob",
    mouthStyle: "steps",
    mouthKeys: nKeys,
    mouthDepth: bass ? 0.03 : 0.06,
    squashScale: bass ? 1.7 : 1.5,
    faceInk: CREAM,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();

  // Power-cable tail with a plug (tail trait = cable length).
  const cable = limb(back, rx * 0.85, cy + ry * 0.55, "tail", 1, 0, 0);
  limbs.push(cable.l);
  drawers.push(() => {
    const g = cable.g;
    g.clear();
    const L = H * 0.3 * gn.tailLength;
    const floor = -(cy + ry * 0.55) - H * 0.02;
    const pts = [
      0,
      0,
      L * 0.45,
      Math.min(H * 0.08, floor),
      L * 0.95,
      Math.min(-L * 0.05, floor),
      L * 0.85,
      -L * 0.45,
      L * 1.05,
      -L * 0.62,
    ];
    noodle(g, pts, H * 0.03, CABLE, ow * 0.8, true);
    const px = L * 1.05,
      py = -L * 0.62;
    g.roundRect(px - H * 0.032, py - H * 0.08, H * 0.064, H * 0.08, H * 0.016)
      .fill({ color: RUBBER })
      .stroke({ width: ow * 0.8, color: INK });
    for (const s of sides)
      noodle(g, [px + s * H * 0.014, py - H * 0.08, px + s * H * 0.014, py - H * 0.125], H * 0.012, SILVER, ow * 0.5);
  });

  // Sturdy rubber feet.
  const footW = H * (bass ? 0.2 : 0.14) * (0.85 + 0.3 * gn.span * 0.5);
  for (const s of sides) {
    const foot = limb(back, s * rx * (bass ? 0.68 : 0.62), -legH - H * 0.03, "leg", s, 0, 0);
    limbs.push(foot.l);
    drawers.push(() => {
      foot.g.clear();
      foot.g
        .roundRect(-footW / 2, 0, footW, legH + H * 0.03, H * 0.035)
        .fill({ color: RUBBER })
        .stroke({ width: ow, color: INK });
    });
  }

  // Knob crown: seeded pointer angles; more knobs on wider / more colourful machines.
  const rng = new Rng((gn.seed ^ 0x808) >>> 0);
  const angles = Array.from({ length: 9 }, () => rng.range(-2.2, 2.2));
  const knobR = H * (bass ? 0.04 : 0.036);
  const knobXs: number[] = [];
  if (bass) {
    const inner = eyeDx - eyeR - knobR * 1.2;
    if (inner > 0) knobXs.push(0);
    const outer = eyeDx + eyeR + knobR * 1.5;
    if (outer < rx - knobR) for (const s of sides) knobXs.push(s * outer);
    if (tr.colorfulness >= 0.5 && outer + knobR * 2.6 < rx - knobR)
      for (const s of sides) knobXs.push(s * (outer + knobR * 2.6));
  } else {
    const n = 5 + (tr.width >= 0.5 ? 1 : 0) + (gn.palette.length >= 2 ? 1 : 0);
    for (let i = 0; i < n; i++) knobXs.push(-rx * 0.78 + (i / (n - 1)) * rx * 1.56);
  }
  const knobCap = (c: Colors, i: number) => (gn.markings.multi ? pal(c, i) : i % 2 ? CREAM : SILVER);

  const drawBody = (c: Colors) => {
    const g = body;
    g.clear();
    drawMohawk(g, gn, 0, cy - ry, H, c, ow);
    drawTufts(g, gn, 0, cy - ry, rx * 1.2, H, c.base, ow);
    drawBackSpikes(g, gn, 0, cy + ry * 0.3, rx * 0.95, ry * 1.25, H, c, ow, -0.9, -0.1);
    // Bass: fat sub-woofer belly bulging out below the chassis (drawn first, chassis covers its top).
    if (bass) disc(g, 0, spY, spR * 1.1, c.base, ow);
    // Chassis + lighter top bevel + accent pinstripe.
    g.roundRect(-rx, cy - ry, rx * 2, ry * 2, corner)
      .fill({ color: c.base })
      .stroke({ width: ow, color: INK });
    g.roundRect(-rx + ow * 0.5, cy - ry + ow * 0.5, rx * 2 - ow, ry * 0.32, corner * 0.8).fill({
      color: mix(c.base, 0xffffff, 0.12),
    });
    const stripeY = cy - ry + ry * 0.38;
    const nSeg = 1 + Math.min(3, gn.palette.length);
    for (let k = 0; k < nSeg; k++) {
      const x0 = -rx * 0.9 + (k / nSeg) * rx * 1.8,
        x1 = -rx * 0.9 + ((k + 1) / nSeg) * rx * 1.8;
      g.moveTo(x0, stripeY)
        .lineTo(x1, stripeY)
        .stroke({ width: Math.max(1, ow * 0.55), color: k === 0 ? c.accent : pal(c, k - 1), cap: "butt" });
    }
    // Corner screws.
    for (const sx of sides)
      for (const sy of sides)
        g.circle(sx * (rx - H * 0.035), cy + sy * (ry - H * 0.035), H * 0.012).fill({
          color: mix(c.base, 0xffffff, 0.35),
        });
    if (!bass) {
      // Light key bed behind the step-key grin.
      g.roundRect(
        -mouthW / 2 - H * 0.04,
        mouthY - keys.kh * 0.85,
        mouthW + H * 0.08,
        keys.kh * 1.7 + H * 0.01,
        H * 0.025,
      ).fill({ color: mix(c.base, 0xffffff, 0.2) });
    } else {
      // Sub-woofer surround (the cone itself pumps in the front layer).
      disc(g, 0, spY, spR * 0.9, mix(c.base, INK, 0.35), ow);
      g.circle(0, spY, spR * 0.78).stroke({ width: Math.max(1.2, spR * 0.09), color: SILVER });
      for (let k = 0; k < 6; k++) {
        const a = (k / 6) * Math.PI * 2 + 0.3;
        g.circle(Math.cos(a) * spR * 0.84, spY + Math.sin(a) * spR * 0.84, H * 0.008).fill({ color: SILVER });
      }
    }
    for (let i = 0; i < knobXs.length; i++)
      drawKnob(g, knobXs[i], cy - ry + H * 0.004, knobR, knobCap(c, i), ow * 0.75, angles[i]);
    drawBlush(g, gn, eyeY + (bass ? eyeR * 0.6 : 0), eyeDx, eyeR);
  };

  // Bass cone (front layer so it can pump).
  const cone = new Graphics();
  if (bass) {
    cone.position.set(0, spY);
    front.addChild(cone);
    drawers.push((c) => {
      cone.clear();
      const r = spR * 0.66;
      disc(cone, 0, 0, r, 0x2a292d, ow * 0.8);
      cone.circle(0, 0, r * 0.68).stroke({ width: Math.max(0.8, ow * 0.4), color: 0x55535a });
      cone.circle(0, 0, r * 0.45).stroke({ width: Math.max(0.8, ow * 0.4), color: 0x55535a });
      disc(cone, 0, 0, r * 0.3, gn.markings.multi ? pal(c, 0) : SILVER, ow * 0.6);
      cone.ellipse(-r * 0.1, -r * 0.1, r * 0.1, r * 0.07).fill({ color: 0xffffff, alpha: 0.7 });
    });
  }

  // Lit step keys (overlay on the grin's keys) — one Graphics per key, alpha animated.
  const lit = new Container();
  lit.position.set(0, mouthY);
  front.addChild(lit);
  const litKeys: Graphics[] = [];
  for (let i = 0; i < nKeys; i++) {
    const k = new Graphics();
    k.alpha = 0;
    lit.addChild(k);
    litKeys.push(k);
  }
  drawers.push(() => {
    for (let i = 0; i < nKeys; i++) {
      const g = litKeys[i];
      g.clear();
      const col = STEP_COLORS[keys.group(i)];
      const x = keys.keyX(i);
      g.roundRect(
        x - keys.gap,
        -keys.kh / 2 - keys.gap,
        keys.kw + keys.gap * 2,
        keys.kh + keys.gap * 2,
        keys.kh * 0.3,
      ).fill({ color: mix(col, 0xffffff, 0.6), alpha: 0.45 });
      g.roundRect(x, -keys.kh / 2, keys.kw, keys.kh, Math.min(keys.kw, keys.kh) * 0.22)
        .fill({ color: mix(col, 0xffffff, 0.55) })
        .stroke({ width: Math.max(0.8, ow * 0.3), color: INK });
      g.roundRect(x + keys.kw * 0.2, -keys.kh * 0.36, keys.kw * 0.6, keys.kh * 0.22, keys.kh * 0.1).fill({
        color: 0xffffff,
        alpha: 0.8,
      });
    }
  });

  // Sound-wave rings (accent), expanding out from behind the chassis.
  const ringY = bass ? spY : cy;
  const ringR = bass ? Math.max(spR * 1.3, ry * 1.1) : Math.max(rx, ry) * 0.9;
  const rings: Graphics[] = [];
  for (let k = 0; k < 2; k++) {
    const g = new Graphics();
    g.position.set(0, ringY);
    g.visible = false;
    back.addChildAt(g, 0); // behind the body: only the part outside the silhouette shows
    rings.push(g);
  }
  drawers.push((c) => {
    rings.forEach((g, k) => {
      g.clear();
      g.circle(0, 0, ringR).stroke({ width: ow * 1.9, color: INK, alpha: 0.8 });
      g.circle(0, 0, ringR).stroke({ width: ow * 0.9, color: k === 0 ? c.accent : pal(c, 0, STEP_COLORS[1]) });
    });
  });

  const trail = 1 + Math.min(2, Math.floor(tr.colorfulness * 2.5));
  const phase0 = ((gn.seed % 997) / 997) * nKeys;
  let head = phase0;
  let lastT = 0;
  const tick: Anatomy["tick"] = (p, t, hitAmt) => {
    const dt = Math.max(0, Math.min(0.1, t - lastT));
    lastT = t;
    const st = p.state;
    const hold = st === "hold";
    const cheer = st === "cheer";
    const sleep = st === "sleep";
    // Chase rate in steps/s: slow soft glow at idle, fast on hit / hold.
    const rate = sleep || cheer ? 0 : hold ? 14 : hitAmt > 0 ? 10 : 1.5;
    head = (head + dt * rate) % nKeys;
    const h = Math.floor(head);
    const base = sleep ? 0 : hold || hitAmt > 0 ? 1 : 0.5 + 0.12 * Math.sin(t * 2 + phase0);
    const flash = hitAmt > 0.75 ? (hitAmt - 0.75) * 4 : 0; // every key flashes on the onset
    for (let i = 0; i < nKeys; i++) {
      let a: number;
      if (cheer) a = Math.max(0, Math.sin(t * 9 - i * 0.7));
      else {
        const d = (h - i + nKeys) % nKeys;
        a = d < trail ? base * (1 - (d / trail) * 0.55) : 0;
        a = Math.max(a, flash);
      }
      litKeys[i].alpha = a;
    }
    // Accent: two expanding sound-wave rings.
    const acc = st === "accent";
    const ph = Math.min(1, Math.max(0, p.phase));
    for (let k = 0; k < rings.length; k++) {
      const q = ph * 1.4 - k * 0.3;
      const on = acc && q > 0 && q < 1;
      rings[k].visible = on;
      if (on) {
        const s = 0.9 + 1.0 * q;
        rings[k].scale.set(s, s);
        rings[k].alpha = 1 - q;
      }
    }
    if (bass) {
      // Cone: slow breath at idle (~0.5 Hz), punch out on hits, buzz while holding.
      const s = sleep
        ? 1
        : 1 +
          0.015 * Math.sin(t * 3 + phase0) +
          0.2 * hitAmt * hitAmt +
          (hold ? 0.05 * Math.sin(t * 45) : 0) +
          (cheer ? 0.06 * Math.abs(Math.sin(t * 8)) : 0);
      cone.scale.set(s, s);
    }
  };
  return { geo, back, body, front, limbs, drawers, drawBody, tick };
}
